/* eslint-disable */
import { Box } from '@mui/material';
import { Form, message, Spin } from 'antd';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import MainCard from 'components/MainCard';
import { toastConfig } from 'config';
import { isEqual } from 'lodash';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';

import CardAccordion from './CustomComponents/CardAccordion';
import LeaseDetailsShowAll from './CustomComponents/LeaseDetailsShowAll';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import ActionButtons from './CustomComponents/cardSections/ActionButtons';
import debounce from 'lodash/debounce';
import { monthDiff, stateUpdater, utcConverter } from './CustomComponents/LeaseUtils/helper';
import AgreementFiles from './CustomComponents/cardSections/AgreementFiles';
import GeneralLeaseInformation from './Components/TabItems/GeneralLeaseInformation';
import FinancialTermsConditions from './Components/TabItems/FinancialTermsConditions';
import IFRS16FieldsModifications from './Components/TabItems/IFRS16FieldsModifications';
import AccountingInformation from './CustomComponents/cardSections/AccountingInformation';
import LeasePageHeader from '../../console-solution/lease-schedule-v5/LeaseUtils/LeasePageHeader';
import LeaseHistory from './CustomComponents/LeaseUtils/History/LeaseHistory';
import LeaseSchedule from './CustomComponents/cardSections/LeaseSchedule';

// Cache to store lease data for quick access
const leaseDataCache = {};

dayjs.extend(utc);

const Edit = ({ screen = 'EDIT' }) => {
	// Get cache key before component initializes
	const { state } = useLocation();
	const cacheKey = `${state?._id}-${state?.processCode}`;
	const initialCachedData = leaseDataCache[cacheKey];

	const actionButtonsRef = useRef();
	const leaseScheduleRef = useRef();
	const [selectedTab, setSelectedTab] = useState(0);
	const [searchParams] = useSearchParams();
	const leaseDetailsShowAllRef = useRef();
	const handleDataLoadingExecutedRef = useRef(false);

	const [previousFormData, setPreviousFormData] = useState({});

	// Initialize with cached data if available
	const [cachedLeaseData, setCachedLeaseData] = useState(initialCachedData);

	const [
		getSingleLeaseData,
		{
			data: singleLeaseData, //viewData
			isLoading: isViewLoading,
		},
	] = useGetSingleIdentityMutation();

	// Use cached data or API data
	const displayLeaseData = cachedLeaseData || singleLeaseData;

	const statusData = useMemo(() => {
		return Object.keys(displayLeaseData?.parkedData ?? {})?.length === 0 ? displayLeaseData?.data : displayLeaseData?.parkedData;
	}, [displayLeaseData]);
	//constants
	dayjs.extend(utc);
	const [form] = Form.useForm();
	const navigate = useNavigate();

	// Combined States
	const [formState, setFormState] = useState({
		showFormSubmitModal: false,
		submitPayload: null,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed?',
		},
	});

	// Initialize dataFullyLoaded as true if we have cached data
	const [dataFullyLoaded, setDataFullyLoaded] = useState(!!initialCachedData);

	// State to track original values for comparison
	const [originalValues, setOriginalValues] = useState({
		retirementTerm: null,
		initialDiscountRate: null,
		retirementCost: null,
		retirementStartDate: null,
	});

	// Track if a change row has been added during this edit session
	const [changeRowAdded, setChangeRowAdded] = useState(false);

	// Keep track of the latest form values to prevent duplicate processing
	const lastProcessedValues = useRef({});

	// Track the original length of changes array for reverting
	const [originalChangesLength, setOriginalChangesLength] = useState(0);

	//simplified state setter
	const updateFormState = stateUpdater(setFormState);
	//Single States
	const [rowIndex, setRowIndex] = useState(0);

	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};

	const [updateApi, { isLoading: isUpdateLoading, error: updateError, isSuccess: isUpdateSuccess, isError: isUpdateError }] =
		useGetSingleIdentityMutation();

	// Fetch the data when component mounts
	useEffect(() => {
		// Reset tracking states on component mount
		setChangeRowAdded(false);
		lastProcessedValues.current = {};

		// If we don't have cached data or need to reset, show loading
		if (!initialCachedData) {
			setDataFullyLoaded(false);
		}

		// Function to fetch data
		const fetchLeaseData = async () => {
			try {
				const response = await getSingleLeaseData({
					suffixCode: '',
					_id: state?._id ?? '',
					operation: 'get-single',
					processCode: state?.processCode,
				}).unwrap();

				// Store in cache for future use
				if (!initialCachedData || !isEqual(initialCachedData, response)) {
					leaseDataCache[cacheKey] = response;
					setCachedLeaseData(response);
				}
			} catch (error) {
				console.error('Error fetching lease data:', error);
			}
		};

		if (initialCachedData) {
			// If we have cached data, fetch in background without showing loading
			requestAnimationFrame(() => {
				fetchLeaseData();
			});
		} else {
			// No cached data, fetch and show loading
			fetchLeaseData();
		}
	}, [initialCachedData, cacheKey, state?._id, state?.processCode]);

	// Handle API responses and navigation
	useEffect(() => {
		if (isUpdateSuccess) {
			toast.success('Success! Record updated successfully', toastConfig);
			navigate(-2);
		}
		if (isUpdateError) {
			if (updateError?.data?.code === '501') {
				toast.error(updateError?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isUpdateSuccess, updateError, isUpdateError]);

	// Set form values and initialize tracking when data loads
	useEffect(() => {
		// Skip if no data yet
		if (!statusData || Object.keys(statusData).length === 0) return;

		// Map form values from API data
		const viewValues = {
			tabIndex: '1',
			retirementTerm: statusData?.retirementTerm,
			retirementStartDate: dayjs(statusData?.retirementStartDateField),
			initialDiscountRate: statusData?.initialDiscountRate,
			retirementCost: statusData?.retirementCost,
			changes: statusData?.changes?.map((item, index) => {
				const { inputChangeEffectiveDate, ...change } = item;
				return {
					inputChangeEffectiveDate: index === 0 ? dayjs(statusData?.retirementStartDate) : dayjs(inputChangeEffectiveDate),
					// inputChangeDescription: change.inputChangeDescription || 'Initial Retirement',
					inputChangeDiscountRate: change.inputChangeDiscountRate,
					inputChangeRetirementCost: change.inputChangeRetirementCost,
					inputChangeRetirementTerm: change.inputChangeRetirementTerm,
					inputChangeRetirementDate: dayjs(change.inputChangeRetirementDate),
				};
			}),
		};

		// Store the original length of changes array
		setOriginalChangesLength(statusData?.changes?.length || 0);

		// Set row index based on existing changes
		setRowIndex(statusData?.changes?.length || 0);

		// Set form values synchronously for immediate display
		form.setFieldsValue(viewValues);

		// For cached data, initialize immediately to avoid delay
		if (initialCachedData) {
			// Use requestAnimationFrame to ensure it happens after render
			requestAnimationFrame(() => {
				initializeFormTracking();
			});
		} else {
			// Wait for form to update before capturing original values
			// Use requestAnimationFrame instead of setTimeout for better timing
			requestAnimationFrame(() => {
				initializeFormTracking();
			});
		}
	}, [statusData, initialCachedData]);

	// Extracted function to initialize form tracking
	const initializeFormTracking = () => {
		// Store key field values for change detection
		const originalFieldValues = {
			retirementTerm: form.getFieldValue('retirementTerm'),
			initialDiscountRate: form.getFieldValue('initialDiscountRate'),
			retirementCost: form.getFieldValue('retirementCost'),
			retirementStartDate: form.getFieldValue('retirementStartDate'),
		};

		// Also store these in the lastProcessedValues to avoid duplicate processing
		lastProcessedValues.current = { ...originalFieldValues };

		// Store the captured values
		setOriginalValues(originalFieldValues);

		// Mark data as fully loaded AFTER setting original values
		// console.log('Setting dataFullyLoaded to true for edit mode');
		setDataFullyLoaded(true);
	};

	const [calculateData, setCalculateData] = useState({});
	const [selectHistory, setSelectHistory] = useState(null);

	const manageChangeRows = useCallback(() => {
		// Don't detect changes until data is fully loaded
		if (!dataFullyLoaded) {
			// console.log('Skipping change detection - data not loaded');
			return;
		}

		const currentChangeList = form.getFieldValue('changes') || [];
		const maxAllowedRows = originalChangesLength + 1;

		// If we already have more than max allowed rows, trim to the max
		if (currentChangeList.length > maxAllowedRows) {
			// console.log('Too many change rows detected, trimming to max allowed:', maxAllowedRows);
			const trimmedList = currentChangeList.slice(0, maxAllowedRows);
			form.setFieldsValue({ changes: trimmedList });
			setRowIndex(maxAllowedRows);
			// Update tracking state
			setChangeRowAdded(maxAllowedRows > originalChangesLength);
			return;
		}

		// Get current values from form
		const currentValues = {
			retirementTerm: form.getFieldValue('retirementTerm'),
			initialDiscountRate: form.getFieldValue('initialDiscountRate'),
			retirementCost: form.getFieldValue('retirementCost'),
			retirementStartDate: form.getFieldValue('retirementStartDate'),
		};

		// Check if we've already processed these exact values to prevent duplicate processing
		if (isEqual(currentValues, lastProcessedValues.current)) {
			// console.log('Values already processed, skipping');
			return;
		}

		// Update last processed values
		lastProcessedValues.current = { ...currentValues };

		// Deep compare values to detect changes
		const hasRetirementCostChanged = currentValues.retirementCost !== originalValues.retirementCost;
		const hasDiscountRateChanged = currentValues.initialDiscountRate !== originalValues.initialDiscountRate;
		// Compare retirementStartDate (which are dayjs objects)
		const hasRetirementDateChanged = !dayjs(currentValues.retirementStartDate).isSame(originalValues.retirementStartDate, 'day');

		// Track if any fields changed
		const hasChanges = hasRetirementCostChanged || hasDiscountRateChanged || hasRetirementDateChanged;

		// Check if we should remove the added row when all changes are reverted
		if (!hasChanges && changeRowAdded && currentChangeList.length > originalChangesLength) {
			// console.log('All changes reverted, removing auto-added row');
			const updatedList = currentChangeList.slice(0, originalChangesLength);
			form.setFieldsValue({ changes: updatedList });
			setRowIndex(originalChangesLength);
			setChangeRowAdded(false);
			message.info('Changes reverted. The added change record has been removed.');
			return;
		}

		// If no changes detected, exit early
		if (!hasChanges) {
			return;
		}

		// Check if we need to handle an existing row vs create new one
		const hasAutoadedRow = currentChangeList.length > originalChangesLength;
		let shouldCreateNewRow = !hasAutoadedRow;

		if (hasAutoadedRow) {
			const lastRow = currentChangeList[currentChangeList.length - 1];
			// If the last row has an effective date set, don't create a new row
			if (lastRow && lastRow.inputChangeEffectiveDate) {
				shouldCreateNewRow = false;
			}
		}

		// Determine the appropriate change description
		// let changeDescription = 'Initial Retirement Values';
		// if (hasRetirementCostChanged && hasDiscountRateChanged && hasRetirementDateChanged) {
		// 	changeDescription = 'Change in Cost, Discount Rate, and Retirement Date';
		// } else if (hasRetirementCostChanged && hasDiscountRateChanged) {
		// 	changeDescription = 'Change in Cost and Discount Rate';
		// } else if (hasRetirementCostChanged && hasRetirementDateChanged) {
		// 	changeDescription = 'Change in Cost and Retirement Date';
		// } else if (hasDiscountRateChanged && hasRetirementDateChanged) {
		// 	changeDescription = 'Change in Discount Rate and Retirement Date';
		// } else if (hasRetirementCostChanged) {
		// 	changeDescription = 'Change in Estimated Retirement Cost';
		// } else if (hasDiscountRateChanged) {
		// 	changeDescription = 'Change in Discount Rate';
		// } else if (hasRetirementDateChanged) {
		// 	changeDescription = 'Change in Retirement Date';
		// }

		// Create the row data
		const newRow = {
			inputChangeRetirementCost: currentValues.retirementCost,
			inputChangeDiscountRate: currentValues.initialDiscountRate,
			inputChangeRetirementTerm: currentValues.retirementTerm,
			// inputChangeDescription: changeDescription,
			inputChangeRetirementDate: currentValues.retirementStartDate,
		};

		if (shouldCreateNewRow) {
			// Only create a new row if we haven't already added one
			if (currentChangeList.length < maxAllowedRows) {
				newRow.inputChangeEffectiveDate = null;
				const updatedList = [...currentChangeList, newRow];
				form.setFieldsValue({ changes: updatedList });
				setRowIndex(updatedList.length);
				setChangeRowAdded(true);

				// Mark the effective date field as required
				requestAnimationFrame(() => {
					const newRowIndex = updatedList.length - 1;
					form.setFields([
						{
							name: ['changes', newRowIndex, 'inputChangeEffectiveDate'],
							errors: ['Effective Date is required'],
							touched: true,
						},
					]);
				});

				message.info('A new change record has been added. Please set the effective date.');
			}
		} else {
			// Update the existing added row
			const lastRowIndex = currentChangeList.length - 1;
			const lastRow = currentChangeList[lastRowIndex];

			// Preserve the effective date and other fields not directly edited here
			if (lastRow) {
				newRow.inputChangeEffectiveDate = lastRow.inputChangeEffectiveDate;
				// Preserve other fields from lastRow if necessary
				// Example: newRow.someOtherField = lastRow.someOtherField;
			}

			const updatedList = [...currentChangeList];
			updatedList[lastRowIndex] = newRow;
			form.setFieldsValue({ changes: updatedList });

			// Notify if description changed
			// if (lastRow && lastRow.inputChangeDescription !== changeDescription) {
			// 	message.info(`Change type updated to "${changeDescription}"`);
			// }
		}
	}, [form, originalValues, changeRowAdded, dataFullyLoaded, originalChangesLength, lastProcessedValues]);

	// Use a debounced version of the change handler to avoid rapid consecutive calls
	const debouncedManageChangeRows = useMemo(() => debounce(manageChangeRows, 300), [manageChangeRows]);

	// Expose debouncedManageChangeRows to the window object so child components can access it
	useEffect(() => {
		if (screen === 'EDIT') {
			window.debouncedManageChangeRows = debouncedManageChangeRows;
		}

		// Cleanup function to remove it from window when component unmounts
		return () => {
			if (screen === 'EDIT' && window.debouncedManageChangeRows === debouncedManageChangeRows) {
				delete window.debouncedManageChangeRows;
			}
		};
	}, [screen, debouncedManageChangeRows]);

	function compareAndRemoveDuplicates(inputArray) {
		// Check if the array has at least 2 elements
		if (!Array.isArray(inputArray) || inputArray.length < 2) {
			return {
				data: inputArray,
				isChanged: false,
			};
		}

		// Get the last two elements
		const lastElement = inputArray[inputArray.length - 1];
		const secondLastElement = inputArray[inputArray.length - 2];

		// Function to normalize objects for comparison
		const normalizeForComparison = (arr) => {
			if (!Array.isArray(arr)) return arr;

			return arr.map((item) => {
				// Create a deep copy of the item
				const normalizedItem = JSON.parse(JSON.stringify(item));

				// Normalize each inner object
				return normalizedItem.map((obj) => {
					const result = { ...obj };

					// Normalize amount field if it exists - remove commas and standardize format
					if (obj.amount !== undefined && typeof obj.amount === 'string') {
						// Remove commas and convert to a number, then format back to string with 2 decimal places
						const numericValue = parseFloat(obj.amount.replace(/,/g, ''));
						if (!isNaN(numericValue)) {
							result.amount = numericValue.toFixed(2);
						}
					}

					return result;
				});
			});
		};

		// Normalize both arrays for comparison
		const normalizedLast = normalizeForComparison([lastElement])[0];
		const normalizedSecondLast = normalizeForComparison([secondLastElement])[0];

		// Deep compare the normalized elements
		const areEqual = JSON.stringify(normalizedLast) === JSON.stringify(normalizedSecondLast);

		if (areEqual) {
			// If they are the same, remove the last element
			const modifiedArray = inputArray.slice(0, -1);
			return {
				data: modifiedArray,
				isChanged: false,
			};
		} else {
			// If they are different, return the original array
			return {
				data: inputArray,
				isChanged: true,
			};
		}
	}
	const handleSubmit = async () => {
		const formData = form.getFieldsValue();
		const changes = formData.changes || [];
		if (!changes.length) throw new Error('No changes found');

		// Get row 0's effective date
		const row0EffectiveDate = changes[0]?.inputChangeEffectiveDate || formData?.retirementStartDate;

		const processedChanges = changes.map((change, idx) => {
			const rowiEffectiveDate = change.inputChangeEffectiveDate || row0EffectiveDate;
			const rowiRetirementDate = change.inputChangeRetirementDate || formData?.retirementStartDate;
			const inputChangeMonth = Math.abs(monthDiff(row0EffectiveDate, rowiEffectiveDate)) + 1;
			const inputChangeRetirementTerm = Math.abs(monthDiff(row0EffectiveDate, rowiRetirementDate));
			return {
				...change,
				inputChangeMonth,
				inputChangeRetirementTerm,
				inputChangeEffectiveDate: utcConverter(rowiEffectiveDate),
			};
		});

		const editPagePayload = {
			retirementStartDate: formData?.retirementStartDate,
			retirementTerm: formData?.retirementTerm,
			initialDiscountRate: formData?.initialDiscountRate,
			retirementCost: formData?.retirementCost,
			startYear: formData?.retirementStartDate ? dayjs(formData?.retirementStartDate).year() : null,
			startMonth: formData?.retirementStartDate ? dayjs(formData?.retirementStartDate).month() + 1 : null,
			startDate: formData?.retirementStartDate ? dayjs(formData?.retirementStartDate).date() : null,
			inputChangeMonth: '1', // Default value for API
			changes: processedChanges,
		};

		updateFormState('showFormSubmitModal', true);
		updateFormState('modalData.head', 'Confirmation');
		updateFormState('modalData.label', 'Do you want to update this lease record?');
		updateFormState('submitPayload', editPagePayload);
	};

	const handleModalSubmit = () => {
		updateApi({
			operation: 'update-single',
			processCode: state?.processCode,
			suffixCode: '',
			data: formState?.submitPayload,
			_id: state._id,
			version: +singleLeaseData?.version,
		});
	};
	const hasFormDataChanged = () => {
		const currentFormData = form.getFieldsValue();

		// For VIEW mode, we need more specific field checking
		if (screen?.toLowerCase() !== 'create') {
			const currentPayload = {
				retirementTerm: currentFormData?.changes?.[0]?.inputChangeRetirementTerm,
				discountRate: currentFormData?.changes?.[0]?.inputChangeDiscountRate,
				retirementStartDate: currentFormData?.retirementStartDate?.format('YYYY-MM-DD'),
				retirementCost: currentFormData?.retirementCost,
				initialDiscountRate: currentFormData?.initialDiscountRate,
			};

			const prevPayload = {
				...previousFormData,
			};

			// Compare payloads as JSON strings
			const currentStr = JSON.stringify(currentPayload);
			const prevStr = JSON.stringify(prevPayload);

			if (currentStr !== prevStr) {
				setPreviousFormData(currentPayload);
				return true;
			}
			return false;
		}

		// For CREATE mode
		const currentPayload = {
			retirementTerm: currentFormData?.retirementTerm,
			discountRate: currentFormData?.initialDiscountRate,
			retirementStartDate: currentFormData?.retirementStartDate?.format('YYYY-MM-DD'),
			retirementCost: currentFormData?.retirementCost,
		};

		const currentStr = JSON.stringify(currentPayload);
		const prevStr = JSON.stringify(previousFormData);

		if (currentStr !== prevStr) {
			setPreviousFormData(currentPayload);
			return true;
		}
		return false;
	};
	// MUI Tab handler
	const selectTabHandler = (event, newValue) => {
		setSelectedTab(newValue);
		// Get the tab label and format it for URL
		// const tabLabel = tabItem[newValue]?.label.toLowerCase().replace(/\s+/g, '-');
		debouncedManageChangeRows();
		if (newValue === 1 && dataFullyLoaded) {
			if (leaseScheduleRef.current?.queryV3Loader) {
				leaseScheduleRef.current.queryV3Loader();
			}
		}

		if (newValue === 4 && dataFullyLoaded) {
			if (leaseDetailsShowAllRef.current?.loadData) {
				// Check if form data has changed
				leaseDetailsShowAllRef.current.loadData();
			}
		}
	};
	useEffect(() => {
		if (dataFullyLoaded) {
			if (actionButtonsRef.current?.flag?.check !== 0) {
				// Check if form data has changed
				if (hasFormDataChanged()) {
					actionButtonsRef.current.handleDataLoading();
				}
			} else {
				actionButtonsRef.current.queryV3Loader();
			}
		}
	}, [dataFullyLoaded, screen]);

	const tabItem = [
		{
			key: '1',
			label: 'Retirement Details',
			children: (
				<>
					<CardAccordion
						title="Basic Information"
						darkTitle={true}
						sx={
							{
								// mb: 2,
							}
						}
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
					>
						<GeneralLeaseInformation screen={screen} />
					</CardAccordion>

					<FinancialTermsConditions screen={screen} />
					<IFRS16FieldsModifications screen={screen} rowIndex={rowIndex} initialChangeLength={originalChangesLength} />
					<ActionButtons
						screen={screen}
						statusData={statusData}
						setCalculateData={setCalculateData}
						handleUpdate={handleSubmit}
						viewData={singleLeaseData}
						selectHistory={setSelectHistory}
						dataFullyLoaded={dataFullyLoaded}
						ref={actionButtonsRef}
					/>
				</>
			) || <></>,
		},
		{
			key: '2',
			label: 'Retirement Schedule',
			children: (
				<>
					<LeaseSchedule screen={screen} viewData={singleLeaseData} dataFullyLoaded={dataFullyLoaded} ref={leaseScheduleRef} />
				</>
			),
		},
		{
			key: '3',
			label: 'Modification History',
			children: (
				<>
					<LeaseHistory viewData={singleLeaseData} selectHistory={setSelectHistory} />
				</>
			),
		},
		{
			key: '4',
			label: 'Accounts Assignment',
			children: (
				<>
					<AccountingInformation screen={screen} identityId={singleLeaseData?._id} />
				</>
			),
		},
		{
			key: '5',
			label: 'General Ledgers',
			children: (
				<>
					<CardAccordion
						title="General Ledger View"
						darkTitle={true}
						sx={{
							mt: 2,
							mb: 2,
						}}
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
							p: '10px',
							fontSize: '16px !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
					>
						<LeaseDetailsShowAll ref={leaseDetailsShowAllRef} />
					</CardAccordion>
				</>
			),
		},
		{
			key: '6',
			label: 'Agreements',
			children: (
				<>
					<CardAccordion
						title="Agreements"
						customHeaderSX={{
							backgroundColor: '#e1f0ff !important',
							p: '10px',
						}}
						typographySX={{ fontSize: '16px !important' }}
						darkTitle={true}
						sx={{ mb: 2 }}
					>
						<AgreementFiles screen={screen} />
					</CardAccordion>
				</>
			),
		},
	];

	// Only show spinner if no cached data is available or updating
	const showSpinner = isUpdateLoading || (isViewLoading && !initialCachedData);

	// Handle form value changes to trigger change row management
	const handleFormValuesChange = (changedValues, allValues) => {
		// Call the debounced function to manage change rows based on main field edits.
		// manageChangeRows will compare current form values to original values
		// and add/update/remove a modification row in the 'changes' table accordingly.
		debouncedManageChangeRows();
	};

	return (
		<Box>
			<Spin spinning={showSpinner} style={{ maxHeight: '100%' }}>
				{/*<DetailPageHeader image={solution} title="Edit Lease Record" />*/}
				<LeasePageHeader
					title="Modify Decommission Asset"
					tabsItem={tabItem}
					selectedTab={selectedTab}
					selectTabHandler={selectTabHandler}
					createBtn={false}
				/>
				<MainCard sx={{ overflow: 'visible' }}>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						autoComplete="off"
						requiredMark={false}
						onValuesChange={handleFormValuesChange}
						//  preserve={true}
					>
						<AntdValuePreview form={form} show={false} />
						{/* <Box sx={{ mt: 2 }}>
							{tabItem?.map((item, index) => (
								<TabPanel key={index} value={selectedTab} index={index}>
									<Box sx={{ mt: 2 }}>{tabItem[selectedTab]?.children}</Box>
								</TabPanel>
							))}
						</Box> */}
						<Box sx={{ mt: 2 }}>
							{tabItem?.map((item, index) => (
								<Box
									key={index}
									style={{
										display: selectedTab === index ? 'block' : 'none',
									}}
								>
									{item.children}
								</Box>
							))}
						</Box>
					</Form>
				</MainCard>

				{formState?.showFormSubmitModal && (
					<ComptrollerConfirmModal
						showModal={formState?.showFormSubmitModal}
						modalData={formState?.modalData}
						setShowModal={setShowFormSubmitModal}
						handleSubmit={handleModalSubmit}
					/>
				)}
				{selectHistory && (
					<IdentityCaseActivityModal
						title={state?.processName}
						setSelectHistory={setSelectHistory}
						selectItem={selectHistory}
						processCode={singleLeaseData?.processCode}
						processVersion={singleLeaseData?.parkProcessVersion}
					/>
				)}
			</Spin>
		</Box>
	);
};

export default Edit;
